import React, { useState, useEffect, useMemo } from 'react'

import { WarningOutlined } from '@ant-design/icons'
import useSmartBack from '@hooks/shared/useSmartBack'
import {
  Form,
  Select,
  Button,
  Space,
  Table,
  Alert,
  Input,
  Typography,
  Divider,
  Checkbox,
  message,
  Modal
} from 'antd'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import SelectNotFoundContent from '@components/shared/ui/SelectNotFoundContent'
import ShelfLocationTemplateInput from './ShelfLocationTemplateInput'
import { warehouseZoneService } from '@services/operator/warehouseZoneService'
import shelfTypeService from '@services/operator/shelfTypeService'

const { Title, Text } = Typography
const MAX_SHELF_CREATION_LIMIT = 5000

export default function ShelveBatchNewForm({
  shelfService,
  role = 'admin',
  warehouse = null,  // 固定的 Warehouse（Operator 綁定）
  shelfTypes = [],
  onSubmit,
  addBackgroundJob, // 新增：背景任務管理
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const goBack = useSmartBack()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)

  // Conflict modal state
  const [conflictModalVisible, setConflictModalVisible] = useState(false)
  const [conflictData, setConflictData] = useState(null)

  // Form states
  const [selectedShelfType, setSelectedShelfType] = useState(null)
  const [temperature, setTemperature] = useState('ambient')
  const [rowRange, setRowRange] = useState({ from: '1', to: '1' })
  const [columnRange, setColumnRange] = useState({ from: '1', to: '1' })
  const [layerRange, setLayerRange] = useState({ from: '1', to: '1' })
  const [gridRange, setGridRange] = useState({ from: '', to: '' })
  const [locationTemplate, setLocationTemplate] = useState('{row}-{column}-{layer}')
  const [forPicking, setForPicking] = useState(false)
  const [locked, setLocked] = useState(false)
  const [savingTemplate, setSavingTemplate] = useState(false)
  const [selectedZone, setSelectedZone] = useState(null)
  const [zones, setZones] = useState([])
  const [zonesLoading, setZonesLoading] = useState(false)

  const [localShelfTypes, setLocalShelfTypes] = useState(shelfTypes)

  // 載入預設模板
  useEffect(() => {
    if (warehouse?.default_shelf_location_template) {
      setLocationTemplate(warehouse.default_shelf_location_template)
    }
  }, [warehouse])

  useEffect(() => {
    setLocalShelfTypes(shelfTypes)
  }, [shelfTypes])

  // 載入倉庫區域
  const fetchZones = async () => {
    setZonesLoading(true)
    try {
      const response = await warehouseZoneService.getRecords({ page_size: 200 })
      if (response.success) {
        setZones(response.data || [])
      }
    } catch (error) {
      console.error('Failed to fetch zones:', error)
    } finally {
      setZonesLoading(false)
    }
  }

  useEffect(() => {
    fetchZones()
  }, [])

  // 重新載入儲位類型
  const handleReloadShelfTypes = async () => {
    const refreshed = await shelfTypeService.listAll()
    if (refreshed.data) {
      setLocalShelfTypes(refreshed.data)
    }
  }

  // 儲存為預設模板
  const handleSaveAsDefault = async (template) => {
    setSavingTemplate(true)
    try {
      const data = await shelfService.updateDefaultTemplate(template)

      if (data.success) {
        message.success(t('shared.shelves.batch_new.template.save_success'))
      } else {
        message.error(data.message || t('common.error.operation_failed'))
      }
    } catch (error) {
      console.error('Save template error:', error)
      message.error(t('common.error.network_error'))
    } finally {
      setSavingTemplate(false)
    }
  }

  // 定義有效字元範圍（0-9 和 A-Z，按順序）
  const VALID_CHARS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ'

  // 檢查是否為純數字
  const isNumeric = (str) => /^\d+$/.test(str)

  // 當兩端都是純數字但長度不同時，自動補零對齊
  const normalizeRange = (from, to) => {
    const fromStr = String(from).trim().toUpperCase()
    const toStr = String(to).trim().toUpperCase()

    if (fromStr.length === toStr.length) return [fromStr, toStr]

    // 檢查是否都是純數字
    if (isNumeric(fromStr) && isNumeric(toStr)) {
      const maxLen = Math.max(fromStr.length, toStr.length)
      return [fromStr.padStart(maxLen, '0'), toStr.padStart(maxLen, '0')]
    }

    return [fromStr, toStr]
  }

  // 純數字範圍的計數
  const getNumericRangeCount = (fromStr, toStr) => {
    const fromNum = parseInt(fromStr, 10)
    const toNum = parseInt(toStr, 10)
    if (fromNum > toNum) return 0
    return toNum - fromNum + 1
  }

  // 純數字範圍的值生成
  const generateNumericRangeValues = (fromStr, toStr, maxCount = 1000) => {
    const fromNum = parseInt(fromStr, 10)
    const toNum = parseInt(toStr, 10)
    if (fromNum > toNum) return []
    const padLen = Math.max(fromStr.length, toStr.length)
    const values = []
    for (let i = fromNum; i <= toNum && values.length < maxCount; i++) {
      values.push(String(i).padStart(padLen, '0'))
    }
    return values
  }

  // 為每個位置建立範圍（根據起始和結束值）
  const buildPositionRanges = (fromStr, toStr) => {
    const ranges = []
    const maxLen = Math.max(fromStr.length, toStr.length)

    for (let i = 0; i < maxLen; i++) {
      const fromChar = i < fromStr.length ? fromStr[i] : '0'
      const toChar = i < toStr.length ? toStr[i] : 'Z'

      const fromIndex = VALID_CHARS.indexOf(fromChar)
      const toIndex = VALID_CHARS.indexOf(toChar)

      if (fromIndex === -1 || toIndex === -1) {
        throw new Error(`Invalid character at position ${i}`)
      }

      ranges.push({
        start: fromChar,
        end: toChar,
        startIndex: fromIndex,
        endIndex: toIndex,
        validChars: VALID_CHARS.slice(fromIndex, toIndex + 1)
      })
    }

    return ranges
  }

  // 遞增字串（基於位置範圍）
  const incrementStringWithRanges = (str, ranges) => {
    const chars = str.split('')
    let carry = true

    for (let i = chars.length - 1; i >= 0 && carry; i--) {
      const range = ranges[i]
      const currentIndex = range.validChars.indexOf(chars[i])

      if (currentIndex === -1) {
        throw new Error(`Character ${chars[i]} not in valid range for position ${i}`)
      }

      if (currentIndex === range.validChars.length - 1) {
        // 到達該位置的最大值，進位
        chars[i] = range.validChars[0]
        carry = true
      } else {
        // 遞增
        chars[i] = range.validChars[currentIndex + 1]
        carry = false
      }
    }

    // 如果還有 carry，表示超出範圍
    if (carry) {
      return null  // 超出範圍
    }

    return chars.join('')
  }

  // 計算範圍數量（基於位置範圍）
  const getRangeCount = (from, to) => {
    if (!from || !to) return 0

    const [fromStr, toStr] = normalizeRange(from, to)

    // 純數字走數值邏輯
    if (isNumeric(fromStr) && isNumeric(toStr)) {
      return getNumericRangeCount(fromStr, toStr)
    }

    // 檢查長度是否一致
    if (fromStr.length !== toStr.length) {
      console.warn('Start and end must have same length')
      return 0
    }

    // 使用 ASCII 比較
    if (fromStr > toStr) return 0

    try {
      const ranges = buildPositionRanges(fromStr, toStr)
      let count = 0
      let current = fromStr
      const limit = 10000 // 防止無限循環

      while (current && current <= toStr && count < limit) {
        count++
        if (current === toStr) break
        current = incrementStringWithRanges(current, ranges)
      }

      return count
    } catch (error) {
      console.error('Error calculating range count:', error)
      return 0
    }
  }

  // 檢查範圍是否起始值大於結束值
  const isRangeReversed = (from, to) => {
    if (!from || !to) return false

    const [fromStr, toStr] = normalizeRange(from, to)

    // 嘗試轉成數字比較
    const fromNum = parseInt(fromStr, 10)
    const toNum = parseInt(toStr, 10)

    // 如果兩邊都是純數字，用數字比較
    if (!isNaN(fromNum) && !isNaN(toNum) && String(fromNum) === fromStr && String(toNum) === toStr) {
      return fromNum > toNum
    }

    // 否則用字串比較（長度相同時）
    if (fromStr.length !== toStr.length) return false
    return fromStr > toStr
  }

  // 範圍反向錯誤檢查
  const rangeReversed = useMemo(() => ({
    row: isRangeReversed(rowRange.from, rowRange.to),
    column: isRangeReversed(columnRange.from, columnRange.to),
    layer: isRangeReversed(layerRange.from, layerRange.to),
    grid: isRangeReversed(gridRange.from, gridRange.to)
  }), [rowRange, columnRange, layerRange, gridRange])

  // 計算總數
  const totalCount = useMemo(() => {
    const rowCount = getRangeCount(rowRange.from, rowRange.to)
    const columnCount = getRangeCount(columnRange.from, columnRange.to)
    const layerCount = getRangeCount(layerRange.from, layerRange.to)
    const gridCount = (gridRange.from && gridRange.to) ? getRangeCount(gridRange.from, gridRange.to) : 0

    if (gridCount > 0) {
      return rowCount * columnCount * layerCount * gridCount
    }
    return rowCount * columnCount * layerCount
  }, [rowRange, columnRange, layerRange, gridRange])

  // 獲取當前模板
  const getCurrentTemplate = () => {
    return locationTemplate || '{row}-{column}-{layer}'
  }

  // 格式化數字（支援補零）
  const formatNumber = (num, format) => {
    const match = format.match(/:0(\d+)d/)
    if (match) {
      const width = parseInt(match[1])
      return String(num).padStart(width, '0')
    }
    return String(num)
  }

  // 生成單個 location
  const generatelocation = (row, column, layer, grid = null) => {
    const template = getCurrentTemplate()
    let location = template

    // 替換變數
    location = location.replace(/\{row:0(\d+)d\}/g, (match, width) => formatNumber(row, match))
    location = location.replace(/\{row\}/g, row)
    location = location.replace(/\{column:0(\d+)d\}/g, (match, width) => formatNumber(column, match))
    location = location.replace(/\{column\}/g, column)
    location = location.replace(/\{layer:0(\d+)d\}/g, (match, width) => formatNumber(layer, match))
    location = location.replace(/\{layer\}/g, layer)

    if (grid !== null) {
      location = location.replace(/\{grid:0(\d+)d\}/g, (match, width) => formatNumber(grid, match))
      location = location.replace(/\{grid\}/g, grid)
    }

    return location
  }

  // 生成範圍內的所有值（基於位置範圍）
  const generateRangeValues = (from, to, maxCount = 1000) => {
    if (!from || !to) return []

    const [fromStr, toStr] = normalizeRange(from, to)

    // 純數字走數值邏輯
    if (isNumeric(fromStr) && isNumeric(toStr)) {
      return generateNumericRangeValues(fromStr, toStr, maxCount)
    }

    // 檢查長度是否一致
    if (fromStr.length !== toStr.length) {
      console.warn('Start and end must have same length')
      return []
    }

    try {
      const ranges = buildPositionRanges(fromStr, toStr)
      const values = []
      let current = fromStr
      let count = 0

      while (current && current <= toStr && count < maxCount) {
        values.push(current)
        if (current === toStr) break
        current = incrementStringWithRanges(current, ranges)
        count++
      }

      return values
    } catch (error) {
      console.error('Error generating range values:', error)
      return []
    }
  }

  // 生成預覽資料
  const previewData = useMemo(() => {
    const data = []
    let count = 0
    const limit = 10 // 只顯示前 10 筆

    const rowValues = generateRangeValues(rowRange.from, rowRange.to)
    const columnValues = generateRangeValues(columnRange.from, columnRange.to)
    const layerValues = generateRangeValues(layerRange.from, layerRange.to)
    const gridValues = (gridRange.from && gridRange.to) ? generateRangeValues(gridRange.from, gridRange.to) : []

    const hasGrid = gridValues.length > 0

    for (const row of rowValues) {
      if (count >= limit) break
      for (const column of columnValues) {
        if (count >= limit) break
        for (const layer of layerValues) {
          if (count >= limit) break
          if (hasGrid) {
            for (const grid of gridValues) {
              if (count >= limit) break
              data.push({
                key: `${row}-${column}-${layer}-${grid}`,
                location: generatelocation(row, column, layer, grid),
                row,
                column,
                layer,
                grid,
                temperature,
              })
              count++
            }
          } else {
            data.push({
              key: `${row}-${column}-${layer}`,
              location: generatelocation(row, column, layer),
              row,
              column,
              layer,
              grid: null,
              temperature,
            })
            count++
          }
        }
      }
    }

    return data
  }, [rowRange, columnRange, layerRange, gridRange, warehouse, temperature, locationTemplate])

  // 預覽表格欄位
  const hasGridValues = gridRange.from && gridRange.to

  const previewColumns = [
    {
      title: t('shared.shelves.batch_new.preview.location'),
      dataIndex: 'location',
      key: 'location',
    },
    {
      title: t('shared.shelves.batch_new.preview.row'),
      dataIndex: 'row',
      key: 'row',
      width: 80,
    },
    {
      title: t('shared.shelves.batch_new.preview.column'),
      dataIndex: 'column',
      key: 'column',
      width: 80,
    },
    {
      title: t('shared.shelves.batch_new.preview.layer'),
      dataIndex: 'layer',
      key: 'layer',
      width: 80,
    },
    ...(hasGridValues ? [{
      title: t('shared.shelves.batch_new.preview.grid'),
      dataIndex: 'grid',
      key: 'grid',
      width: 80,
    }] : []),
    {
      title: t('shared.shelves.batch_new.preview.temperature'),
      dataIndex: 'temperature',
      key: 'temperature',
      width: 100,
      render: (temp) => t(`enums.temperature.${temp}`),
    },
  ]

  // 驗證範圍
  const validateRanges = () => {
    const [rowFrom, rowTo] = normalizeRange(rowRange.from, rowRange.to)
    const [columnFrom, columnTo] = normalizeRange(columnRange.from, columnRange.to)
    const [layerFrom, layerTo] = normalizeRange(layerRange.from, layerRange.to)

    if (!rowFrom || !rowTo || rowFrom > rowTo) {
      message.error(t('shared.shelves.batch_new.errors.invalid_row_range'))
      return false
    }
    if (!columnFrom || !columnTo || columnFrom > columnTo) {
      message.error(t('shared.shelves.batch_new.errors.invalid_column_range'))
      return false
    }
    if (!layerFrom || !layerTo || layerFrom > layerTo) {
      message.error(t('shared.shelves.batch_new.errors.invalid_layer_range'))
      return false
    }
    if (totalCount <= 0) {
      message.error(t('shared.shelves.batch_new.errors.no_shelves'))
      return false
    }
    if (totalCount > MAX_SHELF_CREATION_LIMIT) {
      message.error(t('shared.shelves.batch_new.errors.too_many_shelves', { max: MAX_SHELF_CREATION_LIMIT }))
      return false
    }
    return true
  }

  // 建立 payload（共用）
  const buildPayload = (onConflict = null) => {
    const payload = {
      shelf_type_id: selectedShelfType,
      temperature,
      row_range: rowRange,
      column_range: columnRange,
      layer_range: layerRange,
      location_template: getCurrentTemplate(),
      pick_face: forPicking,
      locked,
      warehouse_zone_id: selectedZone || null,
    }

    if (gridRange.from && gridRange.to) {
      payload.grid_range = gridRange
    }

    if (onConflict) {
      payload.on_conflict = onConflict
    }

    return payload
  }

  // 提交處理：先呼叫 preview 檢查重複
  const handleSubmit = async () => {
    if (!validateRanges()) return

    setLoading(true)
    try {
      const payload = buildPayload()
      const preview = await shelfService.previewBatchCreate(payload)

      if (!preview.success) {
        message.error(preview.message || t('common.error.operation_failed'))
        return
      }

      const { existing_count, new_count, inventory_count, total_count } = preview.data

      if (existing_count > 0) {
        // 有重複儲位，顯示衝突處理 Modal
        showConflictModal({ existing_count, new_count, inventory_count, total_count })
      } else if (totalCount > 100) {
        // 無重複但數量多，顯示一般確認
        Modal.confirm({
          title: t('shared.shelves.batch_new.confirm.title'),
          content: t('shared.shelves.batch_new.confirm.content', { count: totalCount }),
          okText: t('common.button.confirm'),
          cancelText: t('common.button.cancel'),
          onOk: () => performSubmit('skip'),
        })
      } else {
        // 無重複，直接建立
        performSubmit('skip')
      }
    } catch (error) {
      console.error('Preview error:', error)
      message.error(t('common.error.network_error'))
    } finally {
      setLoading(false)
    }
  }

  // 衝突處理 Modal
  const showConflictModal = (data) => {
    setConflictData(data)
    setConflictModalVisible(true)
  }

  const handleConflictAction = (onConflict) => {
    setConflictModalVisible(false)
    setConflictData(null)
    performSubmit(onConflict)
  }


  const performSubmit = async (onConflict = 'skip') => {
    setLoading(true)

    try {
      const payload = buildPayload(onConflict)
      const data = await shelfService.batchCreate(payload)

      if (data.success && data.data.job_id) {
        if (addBackgroundJob) {
          addBackgroundJob(data.data.job_id, 'shared.shelves.batch_new.job_title', {
            nameParams: { count: totalCount }
          })
        } else {
          console.error('[ShelveBatchNewForm] addBackgroundJob is not available!')
        }

        message.success(t('shared.shelves.batch_new.job_started'))
        goBack()
      } else {
        message.error(data.message || t('common.error.operation_failed'))
      }
    } catch (error) {
      console.error('Batch create error:', error)
      message.error(t('common.error.network_error'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        {/* 1. 基本資訊 */}
        <Title level={4}>{t('shared.shelves.batch_new.sections.basic_info')}</Title>

        <Form.Item
          label={t('models.shelf.shelf_type')}
          required
        >
          <Select
            value={selectedShelfType}
            onChange={setSelectedShelfType}
            placeholder={t('shared.shelves.batch_new.placeholders.select_shelf_type')}
            options={localShelfTypes.filter(st => !st.system_reserved).map(st => ({ label: st.name, value: st.id }))}
            notFoundContent={
              <SelectNotFoundContent
                hint={t('shared.shelves.batch_new.hints.no_shelf_types')}
                settingsPath="/operator/shelf_types"
                settingsLabel={t('shared.shelves.batch_new.hints.go_to_shelf_type_settings')}
                onReload={handleReloadShelfTypes}
              />
            }
          />
        </Form.Item>

        <Form.Item label={t('models.shelf.temperature')}>
          <Select
            value={temperature}
            onChange={setTemperature}
            options={[
              { label: t('enums.temperature.ambient'), value: 'ambient' },
              { label: t('enums.temperature.controlled'), value: 'controlled' },
              { label: t('enums.temperature.refrigerated'), value: 'refrigerated' },
              { label: t('enums.temperature.frozen'), value: 'frozen' },
              { label: t('enums.temperature.air_conditioned'), value: 'air_conditioned' },
            ]}
          />
        </Form.Item>

        <Form.Item
          label={t('models.shelf.warehouse_zone')}
        >
          <Select
            value={selectedZone}
            onChange={setSelectedZone}
            placeholder={t('shared.shelves.batch_new.placeholders.select_zone')}
            allowClear
            loading={zonesLoading}
            options={zones.map(z => ({ label: z.name, value: z.id }))}
            notFoundContent={
              <SelectNotFoundContent
                hint={t('shared.shelves.batch_new.hints.no_zones')}
                settingsPath={`/operator/warehouses/${warehouse?.id}`}
                settingsLabel={t('shared.shelves.batch_new.hints.manage_zones')}
                onReload={fetchZones}
              />
            }
          />
        </Form.Item>

        <Divider />

        {/* 2. 範圍設定 */}
        <Title level={4}>{t('shared.shelves.batch_new.sections.range_settings')}</Title>

        <Form.Item
          label={t('models.shelf.row_range')}
          help={t('shared.shelves.batch_new.hints.alphanumeric')}
          required
        >
          <Space>
            {t('shared.shelves.batch_new.labels.from')}
            <Input
              style={{ width: 80 }}
              value={rowRange.from}
              onChange={e => setRowRange(prev => ({ ...prev, from: e.target.value }))}
              placeholder={t('shared.shelves.batch_new.placeholders.range_start')}
            />
            {t('shared.shelves.batch_new.labels.to')}
            <Input
              style={{ width: 80 }}
              value={rowRange.to}
              onChange={e => setRowRange(prev => ({ ...prev, to: e.target.value }))}
              placeholder={t('shared.shelves.batch_new.placeholders.range_end')}
            />
          </Space>
        </Form.Item>

        <Form.Item
          label={t('models.shelf.column_range')}
          help={t('shared.shelves.batch_new.hints.alphanumeric')}
          required
        >
          <Space>
            {t('shared.shelves.batch_new.labels.from')}
            <Input
              style={{ width: 80 }}
              value={columnRange.from}
              onChange={e => setColumnRange(prev => ({ ...prev, from: e.target.value }))}
              placeholder={t('shared.shelves.batch_new.placeholders.range_start')}
            />
            {t('shared.shelves.batch_new.labels.to')}
            <Input
              style={{ width: 80 }}
              value={columnRange.to}
              onChange={e => setColumnRange(prev => ({ ...prev, to: e.target.value }))}
              placeholder={t('shared.shelves.batch_new.placeholders.range_end')}
            />
          </Space>
        </Form.Item>

        <Form.Item
          label={t('models.shelf.layer_range')}
          help={t('shared.shelves.batch_new.hints.alphanumeric')}
          required
        >
          <Space>
            {t('shared.shelves.batch_new.labels.from')}
            <Input
              style={{ width: 80 }}
              value={layerRange.from}
              onChange={e => setLayerRange(prev => ({ ...prev, from: e.target.value }))}
              placeholder={t('shared.shelves.batch_new.placeholders.layer_start')}
            />
            {t('shared.shelves.batch_new.labels.to')}
            <Input
              style={{ width: 80 }}
              value={layerRange.to}
              onChange={e => setLayerRange(prev => ({ ...prev, to: e.target.value }))}
              placeholder={t('shared.shelves.batch_new.placeholders.layer_end')}
            />
          </Space>
        </Form.Item>

        <Form.Item
          label={t('models.shelf.grid_range')}
          help={t('shared.shelves.batch_new.hints.grid_optional')}
        >
          <Space>
            {t('shared.shelves.batch_new.labels.from')}
            <Input
              style={{ width: 80 }}
              value={gridRange.from}
              onChange={e => setGridRange(prev => ({ ...prev, from: e.target.value }))}
              placeholder={t('shared.shelves.batch_new.placeholders.range_start')}
            />
            {t('shared.shelves.batch_new.labels.to')}
            <Input
              style={{ width: 80 }}
              value={gridRange.to}
              onChange={e => setGridRange(prev => ({ ...prev, to: e.target.value }))}
              placeholder={t('shared.shelves.batch_new.placeholders.range_end')}
            />
          </Space>
        </Form.Item>

        {/* 範圍反向錯誤提示 */}
        {rangeReversed.row && (
          <Alert
            message={t('shared.shelves.batch_new.errors.invalid_row_range')}
            type="error"
            showIcon
            style={{ marginBottom: 8 }}
          />
        )}
        {rangeReversed.column && (
          <Alert
            message={t('shared.shelves.batch_new.errors.invalid_column_range')}
            type="error"
            showIcon
            style={{ marginBottom: 8 }}
          />
        )}
        {rangeReversed.layer && (
          <Alert
            message={t('shared.shelves.batch_new.errors.invalid_layer_range')}
            type="error"
            showIcon
            style={{ marginBottom: 8 }}
          />
        )}
        {rangeReversed.grid && (
          <Alert
            message={t('shared.shelves.batch_new.errors.invalid_grid_range')}
            type="error"
            showIcon
            style={{ marginBottom: 8 }}
          />
        )}

        <Alert
          message={t('shared.shelves.batch_new.info.total_count', { count: totalCount })}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Divider />

        {/* 3. 編號規則 */}
        <Title level={4}>{t('shared.shelves.batch_new.sections.location_template')}</Title>

        <ShelfLocationTemplateInput
          value={locationTemplate}
          onChange={setLocationTemplate}
          onSave={handleSaveAsDefault}
          saving={savingTemplate}
          showPreview={true}
          previewParams={{
            rowRange,
            columnRange,
            layerRange,
            gridRange
          }}
        />

        <Divider />

        {/* 4. 其他選項 */}
        <Title level={4}>{t('shared.shelves.batch_new.sections.other_options')}</Title>

        <Form.Item>
          <Space direction="vertical">
            <Checkbox
              checked={forPicking}
              onChange={e => setForPicking(e.target.checked)}
            >
              {t('models.shelf.pick_face')}
            </Checkbox>
            <Checkbox
              checked={locked}
              onChange={e => setLocked(e.target.checked)}
            >
              {t('models.shelf.locked')}
            </Checkbox>
          </Space>
        </Form.Item>

        <Divider />

        {/* 5. 預覽 */}
        <Title level={4}>{t('shared.shelves.batch_new.sections.preview')}</Title>

        <Alert
          message={t('shared.shelves.batch_new.info.preview_count', { count: totalCount })}
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Table
          columns={previewColumns}
          dataSource={previewData}
          pagination={false}
          size="small"
          bordered
        />

        {totalCount > 10 && (
          <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            {t('shared.shelves.batch_new.info.showing_first', { count: 10, total: totalCount })}
          </Text>
        )}

        {/* 提交按鈕 */}
        <Form.Item style={{ marginTop: 24 }}>
          <Space>
            <Button onClick={goBack}>
              {t('common.button.cancel')}
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              disabled={!warehouse || !selectedShelfType || totalCount <= 0}
            >
              {t('common.button.create', { count: totalCount })}
            </Button>
          </Space>
        </Form.Item>
      </Form>

      {/* 衝突處理 Modal */}
      <Modal
        open={conflictModalVisible}
        title={<><WarningOutlined style={{ color: '#faad14', marginRight: 8 }} />{t('shared.shelves.batch_new.conflict.title')}</>}
        width={720}
        onCancel={() => { setConflictModalVisible(false); setConflictData(null) }}
        footer={
          <Space>
            <Button onClick={() => { setConflictModalVisible(false); setConflictData(null) }}>
              {t('common.button.cancel')}
            </Button>
            <Button onClick={() => handleConflictAction('update')}>
              {t('shared.shelves.batch_new.conflict.update_existing')}
            </Button>
            <Button type="primary" onClick={() => handleConflictAction('skip')}>
              {t('shared.shelves.batch_new.conflict.skip_existing')}
            </Button>
          </Space>
        }
      >
        {conflictData && (
          <div>
            <Alert
              message={t('shared.shelves.batch_new.conflict.summary', {
                total: conflictData.total_count,
                new_count: conflictData.new_count,
                existing_count: conflictData.existing_count
              })}
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />

            {conflictData.inventory_count > 0 && (
              <Alert
                message={t('shared.shelves.batch_new.conflict.inventory_warning', {
                  count: conflictData.inventory_count
                })}
                type="error"
                showIcon
              />
            )}
          </div>
        )}
      </Modal>

    </>
  )
}
